import React, { useRef, useState } from 'react';
import { Button, CheckboxBase, CommandMenu, Popover, TagEditable } from '@milanote/roma';
import { cx } from '@milanote/roma/utils/cx';
import { DotsHorizontal } from '@untitledui/icons';
import { GridListItem } from 'react-aria-components';
import { useDispatch } from 'react-redux';

import { Label } from '../../../../../../../common/labels/labelTypes';
import { updateLabel } from '../../../../../../../common/labels/labelActions';
import LabelListItemActionsPopover from './LabelListItemActionsPopover';

type Props = {
    label: Label;
    onEditingChange: (labelId: string, nextIsEditing: boolean) => void;
    onEditClick?: (label: Label) => void;
    isSheet?: boolean;
    isSelected?: boolean;
    isEditing: boolean;
};

const LabelListItem: React.FC<Props> = ({
    label,
    onEditingChange,
    onEditClick,
    isSheet = false,
    isSelected = false,
    isEditing = false,
}) => {
    const dispatch = useDispatch();
    const [isActionsOpen, setIsActionsOpen] = useState<boolean>(false);
    const actionsTriggerRef = useRef<HTMLButtonElement>(null);
    const { _id: labelId, name, color } = label;

    const handleEditingChange = (nextIsEditing: boolean) => {
        onEditingChange(labelId, nextIsEditing);
    };

    const handleCommitRename = (nextName: string) => {
        dispatch(updateLabel(labelId, { name: nextName }));
    };

    const handleRename = () => {
        setIsActionsOpen(false);
        handleEditingChange(true);
    };

    const opensEditSheet = isSheet && !!onEditClick;

    const handleActionClick = () => {
        if (opensEditSheet) {
            onEditClick(label);
            return;
        }

        setIsActionsOpen((currentIsOpen) => !currentIsOpen);
    };

    return (
        <GridListItem id={labelId} textValue={name} className="w-full py-0.5 outline-hidden cursor-pointer">
            {(state) => (
                <CommandMenu.Base.Item
                    isSelected={isSelected}
                    isDisabled={state.isDisabled}
                    isFocused={state.isFocused}
                    isFocusVisible={state.isFocusVisible}
                    isHovered={state.isHovered}
                    className="mx-0.5 px-3"
                    size="sm"
                >
                    <div className="flex min-w-0 w-full items-center gap-2">
                        <div className="p-0.5">
                            <CheckboxBase
                                size="sm"
                                variant="form"
                                isSelected={isSelected}
                                isDisabled={state.isDisabled}
                            />
                        </div>

                        <div className="min-w-0 -my-1">
                            <TagEditable
                                dataTagId={labelId}
                                value={name}
                                color={color}
                                size="md"
                                isEditing={isEditing}
                                onEditingChange={handleEditingChange}
                                onCommit={handleCommitRename}
                                className="inline-flex w-auto max-w-full"
                            />
                        </div>

                        <Button
                            ref={actionsTriggerRef}
                            color="tertiary"
                            size="sm"
                            aria-label={`Open actions for ${label.name}`}
                            className="LabelListItemActionsButton ml-auto data-icon-only:p-2 data-icon-only:-my-2 data-icon-only:-mr-2"
                            onClick={handleActionClick}
                            iconLeading={
                                <DotsHorizontal
                                    className={cx('size-4 text-quaternary', isActionsOpen && 'text-fg-primary')}
                                />
                            }
                        />

                        {!opensEditSheet && (
                            <Popover
                                triggerRef={actionsTriggerRef}
                                isOpen={isActionsOpen}
                                onOpenChange={setIsActionsOpen}
                                placement="end top"
                                offset={4}
                                crossOffset={-6}
                                className="w-52"
                            >
                                <LabelListItemActionsPopover
                                    label={label}
                                    onRename={handleRename}
                                    onClose={() => setIsActionsOpen(false)}
                                />
                            </Popover>
                        )}
                    </div>
                </CommandMenu.Base.Item>
            )}
        </GridListItem>
    );
};

export default LabelListItem;
